/**
 * User Cost Breakdown Modal - חלון פירוט עלויות משתמש
 * Shows per-conversation cost breakdown with model details
 */

import { useEffect, useRef, useState } from 'react'
import { useAdminStore } from '../../store/adminStore'
import { X, DollarSign, MessageSquare, Cpu, Clock } from 'lucide-react'
import type { UserCostBreakdown } from '../../types'
import { useDialogA11y } from '../../hooks/useDialogA11y'

interface UserCostModalProps {
  userId: string
  userName: string
  onClose: () => void
}

export default function UserCostModal({ userId, userName, onClose }: UserCostModalProps) {
  const { fetchUserCostBreakdown } = useAdminStore()
  const [data, setData] = useState<UserCostBreakdown | null>(null)
  const [loading, setLoading] = useState(true)
  const dialogRef = useRef<HTMLDivElement>(null)
  useDialogA11y(true, dialogRef, onClose)

  useEffect(() => {
    setLoading(true)
    fetchUserCostBreakdown(userId).then((res) => {
      setData(res)
      setLoading(false)
    })
  }, [userId])

  const getModelShortName = (id: string): string => {
    if (id.includes('opus-4-6')) return 'Opus 4.6'
    if (id.includes('sonnet-4-6')) return 'Sonnet 4.6'
    if (id.includes('opus-4-')) return 'Opus 4'
    if (id.includes('sonnet-4-5')) return 'Sonnet 4.5'
    if (id.includes('sonnet-4-')) return 'Sonnet 4'
    if (id.includes('haiku-4-5')) return 'Haiku 4.5'
    if (id.includes('3-5-sonnet')) return 'Sonnet 3.5'
    if (id.includes('3-5-haiku')) return 'Haiku 3.5'
    if (id.includes('3-opus')) return 'Opus 3'
    if (id.includes('3-sonnet')) return 'Sonnet 3'
    if (id.includes('3-haiku')) return 'Haiku 3'
    return id.replace('claude-', '')
  }

  const formatCost = (cost: number) => {
    if (cost < 0.01) return `$${cost.toFixed(4)}`
    return `$${cost.toFixed(2)}`
  }

  const formatTokens = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
    return n.toString()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-cost-modal-title"
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 id="user-cost-modal-title" className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <DollarSign size={22} className="text-green-600" aria-hidden="true" />
              פירוט עלויות — {userName}
            </h2>
            {data && (
              <p className="text-sm text-gray-500 mt-1">
                סה&quot;כ: <span className="font-bold text-green-700">{formatCost(data.totalCost)}</span>
                {' '} | {data.conversations.length} שיחות
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="סגירה">
            <X size={24} aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              טוען פירוט עלויות...
            </div>
          ) : !data || data.conversations.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              אין שיחות עם עלות למשתמש זה
            </div>
          ) : (
            <div className="space-y-3">
              {data.conversations.map((conv) => (
                <div
                  key={conv.conversationId}
                  className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">
                        {conv.title || 'שיחה ללא כותרת'}
                      </h3>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                        <span className="flex items-center gap-1">
                          <MessageSquare size={12} aria-hidden="true" />
                          {conv.messageCount} הודעות
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={12} aria-hidden="true" />
                          {new Date(conv.lastMessageAt).toLocaleDateString('he-IL')}
                        </span>
                        <span>
                          {formatTokens(conv.totalTokens)} טוקנים
                        </span>
                      </div>
                    </div>
                    <span className={`text-sm font-bold whitespace-nowrap mr-3 ${conv.cost > 0.1 ? 'text-orange-600' : 'text-green-600'}`}>
                      {formatCost(conv.cost)}
                    </span>
                  </div>

                  {/* Model breakdown per conversation */}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Object.entries(conv.models).map(([model, usage]) => (
                      <span
                        key={model}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600"
                        title={`${model}: ${usage.inputTokens} input + ${usage.outputTokens} output`}
                      >
                        <Cpu size={10} className="text-gray-400" aria-hidden="true" />
                        <span className="font-medium">{getModelShortName(model)}</span>
                        <span className="text-gray-400">|</span>
                        <span>{usage.calls}x</span>
                        <span className="text-gray-400">|</span>
                        <span className="text-green-600">{formatCost(usage.cost)}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex justify-end">
          <button onClick={onClose} className="btn btn-primary">סגור</button>
        </div>
      </div>
    </div>
  )
}
